// AutoSaveService.js - Optimized auto-save with debouncing and queue management
import fetchApi from '../components/Lib/api';

class AutoSaveService {
  constructor() {
    this.debounceTimer = null;
    this.saveQueue = new Map();
    this.retryQueue = new Map();
    this.isOnline = navigator.onLine;
    this.maxRetries = 3;
    this.debounceDelay = 2000; // 2 seconds
    this.maxConcurrentSaves = 3;
    
    // Performance tracking
    this.metrics = {
      saveTimes: [],
      errorRates: [],
      queueSizes: []
    };

    // Bind methods
    this.scheduleAutoSave = this.scheduleAutoSave.bind(this);
    this.processSaveQueue = this.processSaveQueue.bind(this);
    this.saveSingleChange = this.saveSingleChange.bind(this);
    
    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processRetryQueue();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    // Final-flush handlers for safety
    this.setupFinalFlushHandlers();
  }

  /**
   * Schedule auto-save with debouncing
   * @param {string} subcategoryId - ID of the subcategory
   * @param {string} changeType - Type of change (content, privacy, files)
   * @param {any} data - The data to save
   */
  scheduleAutoSave(subcategoryId, changeType, data) {
    
    
    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Add to queue
    this.saveQueue.set(subcategoryId, {
      type: changeType,
      data,
      timestamp: Date.now(),
      retryCount: 0
    });

    // Update metrics
    this.metrics.queueSizes.push({
      size: this.saveQueue.size,
      timestamp: Date.now()
    });

    // Schedule save
    this.debounceTimer = setTimeout(() => {
      this.processSaveQueue();
    }, this.debounceDelay);
  }

  /**
   * Process the save queue in parallel chunks
   */
  async processSaveQueue() {
    if (this.saveQueue.size === 0) return;

    const saves = Array.from(this.saveQueue.entries());
    this.saveQueue.clear();

    // Process saves in parallel chunks
    const chunks = this.chunkArray(saves, this.maxConcurrentSaves);
    
    for (const chunk of chunks) {
      const promises = chunk.map(([id, change]) => 
        this.saveSingleChange(id, change)
      );
      
      await Promise.allSettled(promises);
    }
  }

  /**
   * Save a single change
   * @param {string} subcategoryId - ID of the subcategory
   * @param {object} change - Change object with type, data, timestamp
   */
  async saveSingleChange(subcategoryId, change) {
    const startTime = performance.now();
    
    try {
      if (!this.isOnline) {
        throw new Error('Offline - queuing for later');
      }

      

      const response = await fetchApi(
        `/sub_categories/${subcategoryId}/auto_save`,
        'PATCH',
        {
          change_type: change.type,
          data: change.data,
          timestamp: change.timestamp
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Save failed');
      }

      const endTime = performance.now();
      this.trackSaveTime(startTime, endTime, change.type);

      // Mark as saved
      this.markAsSaved(subcategoryId);
      
      // Emit success event
      this.emitSaveEvent('success', {
        subcategoryId,
        changeType: change.type,
        duration: endTime - startTime
      });

    } catch (error) {
      const endTime = performance.now();
      this.trackSaveTime(startTime, endTime, change.type, true);

      // Add to retry queue if not exceeded max retries
      if (change.retryCount < this.maxRetries) {
        this.retryQueue.set(subcategoryId, {
          ...change,
          retryCount: change.retryCount + 1,
          lastError: error.message
        });
        
        this.scheduleRetry(subcategoryId);
      } else {
        // Max retries exceeded
        this.emitSaveEvent('error', {
          subcategoryId,
          changeType: change.type,
          error: error.message,
          duration: endTime - startTime
        });
      }
    }
  }

  /**
   * Schedule retry for failed saves
   * @param {string} subcategoryId - ID of the subcategory
   */
  scheduleRetry(subcategoryId) {
    const retryChange = this.retryQueue.get(subcategoryId);
    if (!retryChange) return;

    // Exponential backoff: 1s, 2s, 4s, 8s...
    const delay = Math.pow(2, retryChange.retryCount) * 1000;
    
    setTimeout(() => {
      if (this.retryQueue.has(subcategoryId)) {
        this.saveSingleChange(subcategoryId, retryChange);
      }
    }, delay);
  }

  /**
   * Process retry queue when coming back online
   */
  async processRetryQueue() {
    if (this.retryQueue.size === 0) return;

    const retries = Array.from(this.retryQueue.entries());
    this.retryQueue.clear();

    for (const [id, change] of retries) {
      await this.saveSingleChange(id, change);
    }
  }

  /**
   * Mark a subcategory as saved
   * @param {string} subcategoryId - ID of the subcategory
   */
  markAsSaved(subcategoryId) {
    // Remove from retry queue
    this.retryQueue.delete(subcategoryId);
    
    // Emit saved event
    this.emitSaveEvent('saved', { subcategoryId });
  }

  /**
   * Track save performance metrics
   * @param {number} startTime - Start time
   * @param {number} endTime - End time
   * @param {string} changeType - Type of change
   * @param {boolean} isError - Whether this was an error
   */
  trackSaveTime(startTime, endTime, changeType, isError = false) {
    const duration = endTime - startTime;
    
    this.metrics.saveTimes.push({
      duration,
      changeType,
      timestamp: Date.now(),
      isError
    });

    // Keep only last 100 metrics
    if (this.metrics.saveTimes.length > 100) {
      this.metrics.saveTimes = this.metrics.saveTimes.slice(-100);
    }

    // Log slow saves
    if (duration > 5000) {
      console.warn(`Slow save detected: ${duration}ms for ${changeType}`);
    }
  }

  /**
   * Emit save events for UI updates
   * @param {string} eventType - Type of event
   * @param {object} data - Event data
   */
  emitSaveEvent(eventType, data) {
    const event = new CustomEvent('autosave', {
      detail: { type: eventType, ...data }
    });
    window.dispatchEvent(event);
  }

  /**
   * Get performance metrics
   */
  getMetrics() {
    const saveTimes = this.metrics.saveTimes;
    const errorCount = saveTimes.filter(s => s.isError).length;
    
    return {
      totalSaves: saveTimes.length,
      errorRate: saveTimes.length > 0 ? (errorCount / saveTimes.length) * 100 : 0,
      averageSaveTime: saveTimes.length > 0 
        ? saveTimes.reduce((sum, s) => sum + s.duration, 0) / saveTimes.length 
        : 0,
      queueSize: this.saveQueue.size,
      retryQueueSize: this.retryQueue.size,
      isOnline: this.isOnline
    };
  }

  /**
   * Utility: Split array into chunks
   * @param {Array} array - Array to chunk
   * @param {number} size - Chunk size
   */
  chunkArray(array, size) {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  /**
   * Force save all pending changes (for manual save)
   */
  async forceSaveAll() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    await this.processSaveQueue();
    await this.processRetryQueue();
  }

  /**
   * Clear all queues (for cleanup)
   */
  clearAll() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    this.saveQueue.clear();
    this.retryQueue.clear();
  }

  /**
   * Setup final-flush handlers for safety
   */
  setupFinalFlushHandlers() {
    // Handle page visibility change (tab switching, minimizing)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && (this.saveQueue.size > 0 || this.retryQueue.size > 0)) {
        this.forceSaveAll();
      }
    });

    // Handle page unload (browser close, navigation)
    window.addEventListener('beforeunload', (event) => {
      if (this.saveQueue.size > 0 || this.retryQueue.size > 0) {
        // Force synchronous save for critical data
        this.forceSaveAllSync();
        
        // Show warning if there are still unsaved changes
        if (this.saveQueue.size > 0 || this.retryQueue.size > 0) {
          event.preventDefault();
          event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
          return event.returnValue;
        }
      }
    });

    // Handle route changes (SPA navigation)
    window.addEventListener('popstate', () => {
      if (this.saveQueue.size > 0 || this.retryQueue.size > 0) {
        this.forceSaveAll();
      }
    });
  }

  /**
   * Force save all pending changes synchronously (for beforeunload)
   */
  async forceSaveAllSync() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    // Process saves immediately without debouncing
    const saves = Array.from(this.saveQueue.entries());
    this.saveQueue.clear();

    // Process saves synchronously for critical data
    for (const [id, change] of saves) {
      try {
        await this.saveSingleChange(id, change);
      } catch (error) {
        // Log error but don't block navigation
        console.error('Final save failed:', error);
      }
    }
  }

  /**
   * Check if there are any pending saves
   */
  hasPendingSaves() {
    return this.saveQueue.size > 0 || this.retryQueue.size > 0;
  }

  /**
   * Get pending save count
   */
  getPendingSaveCount() {
    return this.saveQueue.size + this.retryQueue.size;
  }
}

// Create singleton instance
const autoSaveService = new AutoSaveService();

export default autoSaveService;
